import { useState } from 'react'
import { ExternalLink, Loader2, Send, Sparkles } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { Ticket } from '@/types/ticket'
import { extractErrorMessage, useAdminPatch } from '@/queries/ticketMutations'
import { ticketKeys } from '@/queries/keys'
import { useInvestigations, useStartInvestigation } from '@/queries/investigations'
import { isStale } from '@/types/investigation'
import { StatusSelect } from './StatusSelect'
import { PrioritySelect } from './PrioritySelect'
import { SeveritySelect } from './SeveritySelect'
import { AgreedDateInput } from './AgreedDateInput'
import { SlaClocks } from './SlaClocks'
import { AssigneeCombobox } from './AssigneeCombobox'
import { TrackerControls } from './TrackerControls'
import { Button } from '@/components/ui/button'
import { TICKET_TYPES, typeName } from '@/lib/ticketTypes'
import { CUSTOMER_AREAS } from '@/lib/productAreas'
import { railSelectClass } from './railSelect'
import { api } from '@/lib/api'
import { toast } from 'sonner'

interface ExtendedTicket extends Ticket {
  linearIssueUrl?: string | null
}

// A rail row: micro-muted label on the left, the control on the right, per the
// approved mockup ("Conversation + slim rail"). The controls themselves are
// 30px pill selects — see StatusSelect/PrioritySelect/AssigneeCombobox.
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2.5 text-etn-control">
      <span className="text-etn-text-soft">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

export function AdminControls({ ticket }: { ticket: ExtendedTicket }) {
  const patch = useAdminPatch(ticket._id)
  const qc = useQueryClient()
  const [linearUrl, setLinearUrl] = useState<string | null>(ticket.linearIssueUrl ?? null)

  // The API's own message (e.g. severity on a ticket with no SLA), not ofetch's "400 Bad Request"
  const apply = async (input: Parameters<typeof patch.mutateAsync>[0]) => {
    try { await patch.mutateAsync(input) }
    catch (err) { toast.error(extractErrorMessage(err, 'Update failed')) }
  }

  const linearMutation = useMutation({
    mutationFn: () =>
      api<{ url: string; alreadyLinked: boolean; title?: string; customerWarning?: string }>(`/support-tickets/${ticket._id}/linear`, { method: 'POST', body: {} }),
    onSuccess: data => {
      setLinearUrl(data.url)
      qc.invalidateQueries({ queryKey: ticketKeys.detail(ticket._id) })
      if (data.customerWarning) toast.warning(data.customerWarning)
      else toast.success(data.alreadyLinked ? 'Already linked to Linear' : `Linear issue created: ${data.title ?? 'see link'}`)
    },
    // The API's own message, e.g. that Linear rejected the key, not ofetch's "502 Bad Gateway"
    onError: err => toast.error(extractErrorMessage(err, 'Could not create Linear issue')),
  })

  const investigations = useInvestigations(ticket._id)
  const startInvestigation = useStartInvestigation(ticket._id)
  const investigating = (investigations.data?.data ?? []).some(
    r => (r.status === 'queued' || r.status === 'running') && !isStale(r),
  )

  return (
    <section data-testid="admin-controls">
      <div className="mb-5">
        <Row label="Status">
          <StatusSelect value={ticket.status} displayStatus={ticket.displayStatus} disabled={patch.isPending} trackerStatus={ticket.tracker?.status} onChange={s => apply({ status: s })} />
        </Row>
        <Row label="Priority">
          <PrioritySelect value={ticket.priority} disabled={patch.isPending} onChange={p => apply({ priority: p })} />
        </Row>
        <Row label="Assignee">
          <AssigneeCombobox value={ticket.assignedTo} onChange={id => apply({ assignedTo: id })} />
        </Row>
        <Row label="Type">
          <select aria-label="Type" className={railSelectClass} disabled={patch.isPending} value={ticket.type ?? ''} onChange={e => apply({ type: e.target.value || null })}>
            <option value="">No type</option>
            {ticket.type && !TICKET_TYPES.some(type => type.key === ticket.type) && <option value={ticket.type}>{typeName(ticket.type)}</option>}
            {TICKET_TYPES.map(type => <option key={type.key} value={type.key}>{type.name}</option>)}
          </select>
        </Row>
        <Row label="Area">
          <select aria-label="Area" className={railSelectClass} disabled={patch.isPending} value={ticket.productArea ?? ''} onChange={e => apply({ productArea: e.target.value || null })}>
            <option value="">No area</option>
            {CUSTOMER_AREAS.map(area => <option key={area.key} value={area.key}>{area.name}</option>)}
          </select>
        </Row>
        {ticket.slaSummary && (
          <>
            <Row label="Severity">
              <SeveritySelect
                value={ticket.severity ?? ticket.slaSummary.severity}
                disabled={patch.isPending}
                onChange={s => apply({ severity: s })}
              />
            </Row>
            <Row label="Agreed date">
              <AgreedDateInput
                value={ticket.slaSummary.resolution.agreedDueDate ?? null}
                disabled={patch.isPending}
                onChange={day => apply({ agreedDueDate: day })}
              />
            </Row>
            <SlaClocks summary={ticket.slaSummary} />
          </>
        )}
      </div>

      {/* One stack of full-width secondary buttons — the actions read as a list
          of things to do, rather than as three labelled fields that happen to
          hold a button each. */}
      <div className="mb-5 flex flex-col gap-2">
        <Button variant="secondary" disabled={investigating} onClick={() => startInvestigation.mutate()}>
          {investigating
            ? <><Loader2 className="animate-spin" />Investigating…</>
            : <><Sparkles />Investigate</>}
        </Button>

        {linearUrl ? (
          <Button variant="secondary" asChild>
            <a href={linearUrl} target="_blank" rel="noreferrer">
              <ExternalLink />View Linear issue
            </a>
          </Button>
        ) : (
          <Button variant="secondary" disabled={linearMutation.isPending} onClick={() => linearMutation.mutate()}>
            {linearMutation.isPending
              ? <><Loader2 className="animate-spin" />Drafting…</>
              : <><Send />Create Linear issue</>}
          </Button>
        )}

        <TrackerControls ticket={ticket} />
      </div>
    </section>
  )
}
